import { useSuspenseQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { cn, queries, useAuthConfig } from "@theprelude/core";
import {
  BellIcon,
  Button,
  ExpandDiagonalIcon,
  IconButton,
  Loading,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@theprelude/ds";
import { Suspense } from "react";
import { usePopper } from "@/hooks/use-popper";
import { tb } from "@/lib/tracking";
import { AlertsList } from "../alerts/alerts-list";

export function AlertsDropdown() {
  const dropdown = usePopper();

  return (
    <Popover {...dropdown}>
      <PopoverTrigger asChild>
        <Button
          intent="pill"
          icon={
            <Suspense fallback={<AlertsIcon />}>
              <InteractiveAlertsIcon />
            </Suspense>
          }
          className={cn("bg-header-background", tb("alerts"))}
        />
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="flex max-h-[650px] w-96 flex-col overflow-hidden sm:w-[550px]"
      >
        <div className="flex items-center gap-2 border-stroke-line border-b px-4 py-2">
          <div className="flex-1 font-medium text-lg">Alerts</div>
          <Link to="/account/alerts">
            <IconButton
              icon={<ExpandDiagonalIcon />}
              onClick={() => dropdown.close()}
            />
          </Link>
        </div>
        <div className="flex flex-1 flex-col overflow-hidden">
          <Suspense fallback={<Loading className="my-8" />}>
            <AlertsContent onClick={dropdown.close} />
          </Suspense>
        </div>
      </PopoverContent>
    </Popover>
  );
}

const useAlerts = () => {
  const authConfig = useAuthConfig();

  const { data: alerts } = useSuspenseQuery({
    ...queries.detect.getAlerts(authConfig),
    staleTime: 1000 * 10,
  });

  return alerts;
};

function AlertsIcon() {
  return <BellIcon className="text-white" />;
}

function InteractiveAlertsIcon() {
  const alerts = useAlerts();
  const unreadCount = alerts.filter((a) => !a.read_at).length;

  return (
    <div className="relative">
      <AlertsIcon />
      {unreadCount > 0 && (
        <div className="-right-[2px] absolute top-0 h-[10px] w-[10px] rounded-full border-2 border-header-background bg-notification" />
      )}
    </div>
  );
}

function AlertsContent({ onClick }: { onClick: () => void }) {
  const alerts = useAlerts();

  return <AlertsList alerts={alerts} onClick={onClick} className="p-4" />;
}
